/**
 * AI:
 * Status: "in progress"
 * Type: Component
 * SubType: List
 * Reason: TM-1000002000 — the Consultant shell's "Meine Anträge" as ONE table "Laufende Anträge":
 *         the consultant's Vorgänge (Hauptberater OR Erfasser) without the closed phases. No Reiter,
 *         no Antragsliste, no draft counter, no Antrag link any more ("nur Vorgänge"). The row click
 *         is resolved exclusively through the navigation util, per Vorgang phase.
 */
import {Component, OnDestroy, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {WidgetData} from '../../../../../../core/shared/widget/widget-data';
import {ObjectIdentifier} from '../../../../../../core/shared/basic/object-identifier';
import {ObjectIdentifierData} from '../../../../../../core/shared/basic/object-identifier-data';
import {AgentService} from '../../../../../../feature-core/am/service/api/agent.service';
import {buildCwConsultantCaseTableWidget} from '../../../../widget/cw-case-mine.widget';
import {
    readCwConsultantCaseRef,
    resolveCwConsultantCaseTarget
} from '../../../../util/cw-consultant-case-navigation.util';
import {
    CW_CASE_UNCAPTURED_MARKER_SEVERITY,
    CwCaseUncapturedCountService
} from '../../../../service/cw-case-uncaptured-count.service';

/**
 * „Meine Anträge" of the CONSULTANT shell — ONE table, „Laufende Anträge".
 *
 * <h3>Only Vorgänge (TM-1000002000, capability-matrix row Z1)</h3>
 * The surface used to be two tabs — „Antragsentwürfe" ({@code cw.CwRequest}) and „Laufende Anträge"
 * ({@code cw.CwCase}). The consultant shell now works with Vorgänge only, so what is left is the
 * former second tab as the whole surface: no tabs, no Antrag list, no draft badge, no document
 * counter and no „Eingereicht am" link back to an Antrag.
 *
 * <p>The list itself is unchanged in substance: {@link buildCwConsultantCaseTableWidget} — the
 * Consultant shell's OWN entry point, sharing its query shape and ownership rule (Hauptberater OR
 * Erfasser) with Alpha's {@code cw/case/mine} but under its own alias (compliance finding 7) so a
 * saved column layout on this table can never reshape Alpha's page or vice versa. It is called with
 * NO {@code includedPhases}: the uncaptured Vorgänge (QUICK_QUOTE / DRAFT) are rows of this table,
 * not a separate list — they are only COUNTED separately, by the menu badge
 * ({@code CwCaseUncapturedCountService}, which calls the SAME function WITH that narrowing).
 *
 * <h3>Why this is the WORKING view and Alpha is not</h3>
 * A fixed filter can only narrow: whatever it excludes becomes unreachable on that surface, since a
 * column filter cannot widen a result set again. The closed phases are therefore hidden HERE and stay
 * reachable on the Alpha page {@code cw/case/mine}, which carries no such filter.
 *
 * <h3>Where a row click leads — decided elsewhere, on purpose</h3>
 * {@link handleCaseSelect} holds no phase logic of its own. A resumable QUICK_QUOTE resumes the quick
 * launch; every other row opens the Vorgang on {@code /cw/case/:id} — and that rule lives ONCE, in
 * {@code resolveCwConsultantCaseTarget}, shared with the customer page. A second copy here would be
 * the first one to drift.
 *
 * <p>{@code [disableUpdate]="true"}: this list is for reading, not for inline editing.
 *
 * @ticket TM-1000002000
 */
@Component({
    selector: 'cw-my-requests',
    templateUrl: './cw-my-requests.component.html',
    standalone: false
})
export class CwMyRequestsComponent implements OnInit, OnDestroy {

    protected initialized: boolean = false;

    /**
     * Heading of the one list — the widget's own header text.
     *
     * <p>The shared builder names the widget „Meine Vorgänge", which is right on Alpha
     * {@code cw/case/mine} and wrong here, where the menu entry says „Meine Anträge" and the list is
     * „Laufende Anträge". The name is therefore overwritten on the built INSTANCE in
     * {@link buildWidgets}, never inside the builder, which Alpha reads as well.
     */
    protected readonly caseWidgetTitle: string = 'Laufende Anträge';

    /** The rep's Vorgänge without the closed phases. */
    caseWidget: WidgetData;

    /** The list is a display surface here; nothing is edited in place. */
    disableUpdate: boolean = true;

    agentId: number;
    loadError: string;

    private subscription = new Subscription();

    /** TM-1000002000: the consultant's Vorgänge that have not reached CAPTURED — the red badge beside the heading. */
    uncapturedCount: number = 0;

    /** Severity of that badge, {@code null} at zero — the same rule the menu entry uses. */
    uncapturedSeverity: typeof CW_CASE_UNCAPTURED_MARKER_SEVERITY | null = null;

    // TM-1000002000: the Antrag services (document counter, draft count) are gone with the Antrag list. The
    // uncaptured count is the SAME root service the menu badge reads, so heading and menu cannot disagree.
    constructor(
        protected router: Router,
        protected agentService: AgentService,
        protected uncapturedCountService: CwCaseUncapturedCountService) {
    }

    ngOnInit(): void {
        this.subscription.add(
            this.uncapturedCountService.count$.subscribe(count => {
                this.uncapturedCount = count ?? 0;
                this.uncapturedSeverity = CwCaseUncapturedCountService.markerSeverityOf(count);
            })
        );
        this.uncapturedCountService.refresh();

        this.subscription.add(
            this.agentService.me().subscribe({
                next: (agent) => {
                    this.agentId = agent?.id;
                    if (this.agentId) {
                        this.buildWidgets();
                    } else {
                        this.loadError = 'Der angemeldete Benutzer ist keinem Berater zugeordnet.';
                    }
                    this.initialized = true;
                },
                error: () => {
                    this.loadError = 'Der angemeldete Berater konnte nicht ermittelt werden.';
                    this.initialized = true;
                }
            })
        );
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    /**
     * Builds the one widget — AFTER the agent is known, never before. A builder called with an
     * undefined agent id produces a filter on {@code undefined}, which the backend answers with
     * HTTP 200 and an EMPTY list: the rep would read „nichts vorhanden" with nothing indicating a
     * failure.
     *
     * <h3>⚠ The header row stays ON — do not switch it off</h3>
     * The widget heading is renamed ({@link caseWidgetTitle}), NOT hidden. An earlier revision
     * dropped the whole header with {@code setParamValue('showHeader', false)} and took the
     * three-dot overflow menu with it — the only way into the widget configuration, column layout
     * included — silently and without anything looking broken.
     *
     * <p>TM-1000002000: {@link buildCwConsultantCaseTableWidget} bakes in
     * {@code CW_CASE_CONSULTANT_COLUMNS} (no „Eingereicht am" link column to {@code cw/request/:id})
     * and {@code CW_WORKLIST_CLOSED_PHASES} itself — this call site does not restate either, so it
     * cannot drift from what {@code CwCaseUncapturedCountService} derives its badge from.
     */
    private buildWidgets(): void {
        this.caseWidget = buildCwConsultantCaseTableWidget(this.agentId);
        this.caseWidget.name = this.caseWidgetTitle;
    }

    /**
     * Row click → wherever the shared resolver sends the Vorgang (a QUICK_QUOTE with customer and
     * case type → quick launch with {@code ?caseId=}, EVERY other row → {@code /cw/case/:id}). No id
     * on the row → no navigation.
     *
     * <p>Since 22.09.2026 that target is also where the click ARRIVES: {@code cw/case/:id} carried a
     * phase guard which redirected from CAPTURED on to {@code /cw/case/:id/documents}, so removing
     * the resolver's own {@code /documents} branch alone changed nothing that a user could see. The
     * guard is gone too — see {@code shell-consultant/src/app/cw/cw.route.ts}.
     *
     * <p>Typed as the union {@code mvs-widget}'s {@code (onObjectSelect)} declares. The table emits
     * an {@link ObjectIdentifierData} carrying the row; a bare {@link ObjectIdentifier} has no
     * {@code data}, so the reader falls back to {@code objectId} and treats the phase as unreadable
     * — which lands on the same {@code /cw/case/:id} as everything else.
     *
     * @ticket TM-1000002000
     */
    handleCaseSelect(event: ObjectIdentifier | ObjectIdentifierData): void {
        const target = resolveCwConsultantCaseTarget(readCwConsultantCaseRef(event as ObjectIdentifierData));
        if (target) {
            this.router.navigate(target.commands, target.extras);
        }
    }

}
